import { useState, useEffect, useContext } from 'react'
import Layout from '../components/common/Layout'
import NewShell from '../components/pages-new/NewShell'
import {
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Link as MuiLink,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@material-ui/core'
import { Visibility, VisibilityOff } from '@material-ui/icons'
import ButtonSp from '../components/ui/ButtonSp'
import DividerWithText from '../components/ui/DividerWithText'
import useStyles from '../components/common/loginRegStyle'
import { useForm } from 'react-hook-form'
import Axios from 'axios'
import getConfig from 'next/config'
import Alert from '@material-ui/lab/Alert'
import useSnackBar from '../components/ui/useSnackBar'

import Link from '../components/utility/CustomLink'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import clsx from 'clsx'

import loginNew from '../components/pages-new/LoginNew.module.scss'

import { Context } from '../context'

const { publicRuntimeConfig } = getConfig()

const LoginPage = ({ previousPath, isNew }) => {
  const [isShowingPassword, setShowingPassword] = useState(false)
  const [alreadyLoggedIn, setAlreadyLoggedIn] = useState(false)
  const [isNotConfirmed, setNotConfirmed] = useState(false)
  const [isNeedToSetPassword, setNeedToSetPassword] = useState(false)
  // True from submit until the browser leaves for the SSO redirect, so the
  // button shows a spinner instead of looking frozen. Reset on any failure.
  const [isLoggingIn, setLoggingIn] = useState(false)
  // ?new segmented toggle. The login FORM is identical for both tabs. When
  // 'corporate' is selected a successful login lands in the business app
  // (see getForceIframeUrl) and the signup ("สมัครสมาชิก") link points to
  // /business instead of /register.
  const [customerType, setCustomerType] = useState('personal')
  const classes = useStyles()

  const { onRouting } = useContext(Context)

  const { register, handleSubmit, errors, watch } = useForm()

  const { renderSnackBar, displaySnackBar } = useSnackBar()

  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const { t } = useTranslation('login-signup')
  const Router = useRouter()

  useEffect(() => {
    if (localStorage.getItem('shp_user')) {
      setAlreadyLoggedIn(true)
      displaySnackBar('info', t('already-logged-in'))
      onRouting('/')
    }
  }, [])

  // Back-button restore from bfcache would otherwise leave the spinner on.
  useEffect(() => {
    const onPageShow = (e) => {
      if (e.persisted) setLoggingIn(false)
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [])

  useEffect(() => {
    if (!Router.isReady) return
    if (Router.query.error) {
      displaySnackBar('error', t('login-error'))
    }
  }, [Router.isReady])

  const validPreviousPath = (path) => {
    // after log in will never redirect to those pages
    if (
      path.includes('/confirmed-email') ||
      path.includes('/confirm-email') ||
      path.includes('/login') ||
      path.includes('/register') ||
      path.includes('/verify') ||
      path.includes('/check-auth') ||
      path.includes('/checkprice')
    )
      return publicRuntimeConfig.siteDomain
    return path
  }

  const renderFbGgLogin = () => (
    <Grid container className={classes.socialLogin}>
      <Grid
        item
        xs={12}
        sm={12}
        className={clsx(
          classes.padding1,
          isTabletOrMobile && classes.noPaddingX
        )}
      >
        <a
          href={`${
            publicRuntimeConfig.memberService
          }/sso/login/facebook?isIframe=false&needSSOToken=true&cbUrl=${encodeURIComponent(
            `${
              publicRuntimeConfig.siteDomain
            }/verify?destUrl=${validPreviousPath(previousPath)}`
          )}`}
        >
          <ButtonSp
            variant="outlined"
            className={classes.loginBtn}
            fullWidth
            disabled={alreadyLoggedIn}
          >
            <img
              src="/static/img/login-register/fb-icon.svg"
              className={classes.marginRight1}
            />
            {t('login-with')} Facebook
          </ButtonSp>
        </a>
      </Grid>
    </Grid>
  )

  const getResetPasswordUrl = (email, isSetNewPassword) => {
    const mainUrl = `${publicRuntimeConfig.memberService}/forget-password`
    const destUrl = `${publicRuntimeConfig.siteDomain}`

    const cbUrl = `${
      publicRuntimeConfig.siteDomain
    }/check-auth?destUrl=${encodeURIComponent(destUrl)}`

    return `${mainUrl}?cbUrl=${encodeURIComponent(
      cbUrl
    )}&email=${email}&isSetNewPassword=${
      isSetNewPassword ? 'true' : 'false'
    }&lang=${Router.locale}`
  }

  // Corporate logins (the "ลูกค้าองค์กร" tab, or a B2B/B2B_SHOP mem_type)
  // land in the business app via an EXPLICIT cbUrl to its SSO callback
  // (business.<domain>, derived from appShippop — no new ENV). Never call
  // force-iframe without cbUrl: member then falls back to its own DB
  // (client.default_cb_url), which on prod pointed at business.moaee.com.
  // Regular members keep today's behaviour: cbUrl back to check-auth.
  const B2B_MEM_TYPES = ['B2B', 'B2B_SHOP']

  const getBusinessUrl = () =>
    (publicRuntimeConfig.appShippop || '').replace('://app.', '://business.')

  const getForceIframeUrl = (ssoToken, memType, validatedPreviousPath) => {
    const base = `${publicRuntimeConfig.memberService}/sso/login/force-iframe?ssoToken=${ssoToken}`
    const businessUrl = getBusinessUrl()
    const isCorporate =
      customerType === 'corporate' || B2B_MEM_TYPES.includes(memType)
    if (isCorporate && businessUrl.includes('://business.')) {
      return `${base}&cbUrl=${encodeURIComponent(
        `${businessUrl}/business-login-sso-token/`
      )}`
    }
    const encodedCheckAuthUrl = encodeURIComponent(
      `${publicRuntimeConfig.siteDomain}/check-auth?destUrl=${validatedPreviousPath}`
    )
    return `${base}&cbUrl=${encodedCheckAuthUrl}`
  }

  const onFormSubmit = handleSubmit((data) => {
    if (isLoggingIn) return
    setLoggingIn(true)
    const validatedPreviousPath = validPreviousPath(previousPath)
    Axios.post('/api/auth/auth', data)
      .then((res) => {
        const { status } = res.data
        if (status !== 'success') {
          setLoggingIn(false)
          switch (res.data.code) {
            case 'not_confirm':
              setNotConfirmed(true)
              break
            case 'inactive':
              displaySnackBar('error', t('account-deleted'))
              break
            case 'facebook_reset_password':
              displaySnackBar('error', t('log-in-with-facebook-please'))
              break
            case 'empty_and_reset_password':
              setNeedToSetPassword(true)
              break
            case 'wrong_email':
              displaySnackBar('error', t('email-or-password-wrong'))
              break
            case 'not_found':
              displaySnackBar('error', t('account-not-found'))
              break
            case 'wrong_password':
              displaySnackBar('error', t('wrong-password'))
              break
            case 'wrong_password_b2b':
              displaySnackBar('error', t('wrong-password'))
              break
            default:
              displaySnackBar('error', t('login-error'))
          }
        } else {
          const {
            payload: { ssoToken, mem_type },
          } = res.data
          window.location.replace(
            getForceIframeUrl(ssoToken, mem_type, validatedPreviousPath)
          )
        }
      })
      .catch((err) => {
        setLoggingIn(false)
        displaySnackBar('error', t('login-error'))
      })
  })

  // Shared SEO/meta for both chrome variants.
  const META = {
    title: 'เข้าสู่ระบบ - SHIPPOP ทำเรื่องจัดส่งพัสดุให้เป็นเรื่องง่าย',
    keywords: 'ส่งพัสดุ, ส่งของ, เช็คค่าส่ง, เช็คราคา, เช็คพัสดุ',
    description:
      'เปรียบเทียบราคาการส่งของ เช็คราคา เช็คค่าส่ง บริการขนส่ง ส่งพัสดุ จองขนส่งและจ่ายเงินออนไลน์ ปริ้น แปะ ส่ง รอคนมารับของถึงหน้าบ้าน',
  }

  // Internal nav targets stay clean in the new design (NEW is now default);
  // in the old design they carry ?old so links stay in the old chrome.
  // (Auth/SSO/forgot-password URLs are untouched.)
  const withNew = (path) => (isNew ? path : `${path}?old`)

  // CONTENT is identical for both variants — only the chrome (old Layout vs
  // new-design NewShell = HeaderNew + FooterNew + Sukhumvit) differs.
  const content = (
        <Grid container className={classes.page}>
          {!isTabletOrMobile && (
            <Grid item xs={12} md={5}>
              <img
                src="/static/img/login-register/login.jpg"
                alt="login cover"
                className={classes.coverImg}
              />
            </Grid>
          )}
          <Grid
            item
            container
            xs={12}
            md={7}
            direction="column"
            alignItems="center"
            justify="center"
            className={classes.innerRightSection}
          >
            <h1>{t('login-shippop')}</h1>
            <h2>{t('make-shipping-easy')}</h2>
            <div className={classes.btnsSection}>
              {renderFbGgLogin()}
              <DividerWithText width="100%">{t('or')}</DividerWithText>
              <form className={classes.inputsSection} onSubmit={onFormSubmit}>
                <TextField
                  label={t('email')}
                  name="email"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  inputRef={register({
                    required: true,
                    pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                  })}
                  error={errors.email}
                  helperText={
                    errors.email && t('error.please-fill-email-correctly')
                  }
                  placeholder={t('email-p')}
                />
                <TextField
                  label={t('password')}
                  name="password"
                  variant="outlined"
                  margin="normal"
                  fullWidth
                  type={isShowingPassword ? 'text' : 'password'}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowingPassword(!isShowingPassword)}
                        >
                          {isShowingPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  inputRef={register({ required: true })}
                  error={errors.password}
                  helperText={
                    errors.password && t('error.please-fill-password')
                  }
                  placeholder={t('password-p')}
                />
                <MuiLink
                  href={getResetPasswordUrl(watch('email'), false)}
                  target="_blank"
                  className={classes.forgotPassword}
                >
                  {t('forgot-password')}
                </MuiLink>
                {isNotConfirmed && (
                  <Alert severity="error">
                    {t('confirm-first')}{' '}
                    <Link
                      href={
                        watch('email')
                          ? `/confirm-email?email=${watch('email')}`
                          : '/confirm-email'
                      }
                      passHref
                    >
                      <a>{t('here')}</a>
                    </Link>
                  </Alert>
                )}
                {isNeedToSetPassword && (
                  <Alert severity="error">
                    {t('please-reset-password')}
                    <MuiLink href={getResetPasswordUrl(watch('email'), true)}>
                      {t('here')}
                    </MuiLink>
                  </Alert>
                )}
                <div className={classes.forgotLoginFiller} />
                <ButtonSp
                  className={classes.loginBtn}
                  type="submit"
                  disabled={alreadyLoggedIn || isLoggingIn}
                  aria-busy={isLoggingIn}
                >
                  {isLoggingIn ? (
                    <>
                      <CircularProgress
                        size={18}
                        color="inherit"
                        className={classes.marginRight1}
                      />
                      {t('logging-in')}
                    </>
                  ) : (
                    t('login')
                  )}
                </ButtonSp>
                <p className={classes.toRegister}>
                  {t('if-not-member')}{' '}
                  <Link href={withNew('/register')} passHref>
                    <MuiLink>{t('signup')}</MuiLink>
                  </Link>
                  <br />
                  <Link href={withNew('/privacy')} passHref>
                    <MuiLink>{t('policy')}</MuiLink>
                  </Link>{' '}
                  {t('and-our')}{' '}
                  <Link href={withNew('/terms')} passHref>
                    <MuiLink>{t('terms-of-service')}</MuiLink>
                  </Link>
                </p>
              </form>
            </div>
          </Grid>
        </Grid>
  )

  const loginBullets = [
    {
      title: 'เปรียบเทียบราคาหลายขนส่ง',
      desc: 'เลือกบริการที่เหมาะกับธุรกิจของคุณ',
    },
    {
      title: 'จัดการออเดอร์ได้สะดวก',
      desc: 'รองรับการสร้างรายการส่งทั้งในและต่างประเทศ',
    },
    {
      title: 'พิมพ์ใบปะหน้าได้ทันที',
      desc: 'พร้อมเรียกรถเข้ารับหรือเลือกนำส่งด้วยตัวเอง',
    },
    {
      title: 'ติดตามทุกการจัดส่ง',
      desc: 'ตรวจสอบสถานะพัสดุได้อย่างสะดวก',
    },
  ]

  // ?new-design 2-panel login card (matches the login mockup). It REUSES the
  // exact same auth wiring as the old form: onFormSubmit (email/password),
  // getResetPasswordUrl (forgot password), and withNew() for the
  // register/privacy/terms links. The Facebook button is intentionally
  // omitted here (see TASK 1) — the old (no-?new) page's renderFbGgLogin()
  // is untouched. The corporate toggle sends a successful login to the
  // business app (see getForceIframeUrl).
  const newContent = (
    <div className={loginNew.page}>
      <div className={loginNew.card}>
        {!isTabletOrMobile && (
          <div className={loginNew.left}>
            {/* Copy layers ABOVE the panel's background illustration (z-index),
                over a white scrim for legibility — same pattern as AboutHero. */}
            <div className={loginNew.leftCopy}>
            <h1 className={loginNew.leftHeading}>
              จัดการทุกการส่ง
              <br />
              <span>ให้ธุรกิจเดินหน้าได้ง่ายขึ้น</span>
            </h1>
            <p className={loginNew.leftSub}>
              แพลตฟอร์มจัดส่งครบวงจรสำหรับทุกธุรกิจ เปรียบเทียบราคา
              สร้างรายการส่ง และจัดการพัสดุได้ในที่เดียว
            </p>
            <ul className={loginNew.bullets}>
              {loginBullets.map((b) => (
                <li className={loginNew.bullet} key={b.title}>
                  <svg
                    className={loginNew.check}
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M20 6L9 17l-5-5"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div>
                    <p className={loginNew.bulletTitle}>{b.title}</p>
                    <p className={loginNew.bulletDesc}>{b.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
            </div>
            {/* Background illustration for the whole left panel (see
                .illustrationWrap / .illustration): absolutely positioned to
                fill the panel edge-to-edge, behind the copy. */}
            <picture className={loginNew.illustrationWrap}>
              <source
                srcSet="/static/img/login-new/panel.webp"
                type="image/webp"
              />
              <img
                className={loginNew.illustration}
                src="/static/img/login-new/panel.png"
                alt="รถบรรทุกส่งพัสดุหน้าคลังสินค้าและหมุดตำแหน่ง สื่อถึงการจัดส่งของ SHIPPOP"
                width={941}
                height={1297}
                loading="lazy"
                decoding="async"
              />
            </picture>
          </div>
        )}

        <div className={loginNew.right}>
          <h1 className={loginNew.rightHeading}>
            เข้าสู่ระบบ <span>SHIPPOP</span>
          </h1>
          <p className={loginNew.rightSub}>
            จัดการการจัดส่งของคุณได้ง่าย ครบจบในที่เดียว
          </p>

          {/* Segmented customer-type toggle. Both tabs use the SAME form;
              "ลูกค้าองค์กร" only changes where a successful login lands
              (business app) and the signup link (/business). */}
          <div className={loginNew.toggle} role="tablist" aria-label="ประเภทลูกค้า">
            <button
              type="button"
              role="tab"
              aria-selected={customerType === 'personal'}
              className={clsx(
                loginNew.toggleBtn,
                customerType === 'personal' && loginNew.toggleBtnActive
              )}
              onClick={() => setCustomerType('personal')}
            >
              <span className={loginNew.toggleMain}>ลูกค้าทั่วไป</span>
              <span className={loginNew.toggleSub}>QR / Credit Payment</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={customerType === 'corporate'}
              className={clsx(
                loginNew.toggleBtn,
                customerType === 'corporate' && loginNew.toggleBtnActive
              )}
              onClick={() => setCustomerType('corporate')}
            >
              <span className={loginNew.toggleMain}>ลูกค้าองค์กร</span>
              <span className={loginNew.toggleSub}>Credit term ตามรอบวางบิล</span>
            </button>
          </div>

          <form onSubmit={onFormSubmit}>
            <div className={loginNew.field}>
              <label className={loginNew.label} htmlFor="login-new-email">
                อีเมล
              </label>
              <div className={loginNew.inputWrap}>
                <input
                  id="login-new-email"
                  name="email"
                  type="email"
                  className={clsx(loginNew.input, errors.email && loginNew.inputError)}
                  placeholder="email@example.com"
                  ref={register({
                    required: true,
                    pattern: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
                  })}
                />
              </div>
              {errors.email && (
                <p className={loginNew.errorText}>
                  {t('error.please-fill-email-correctly')}
                </p>
              )}
            </div>

            <div className={loginNew.field}>
              <label className={loginNew.label} htmlFor="login-new-password">
                รหัสผ่าน
              </label>
              <div className={loginNew.inputWrap}>
                <input
                  id="login-new-password"
                  name="password"
                  type={isShowingPassword ? 'text' : 'password'}
                  className={clsx(loginNew.input, errors.password && loginNew.inputError)}
                  placeholder="••••••••"
                  ref={register({ required: true })}
                />
                <button
                  type="button"
                  className={loginNew.eyeBtn}
                  onClick={() => setShowingPassword(!isShowingPassword)}
                  aria-label={isShowingPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                >
                  {isShowingPassword ? <VisibilityOff /> : <Visibility />}
                </button>
              </div>
              {errors.password && (
                <p className={loginNew.errorText}>{t('error.please-fill-password')}</p>
              )}
            </div>

            <div className={loginNew.forgotRow}>
              <MuiLink
                href={getResetPasswordUrl(watch('email'), false)}
                target="_blank"
                className={loginNew.forgotLink}
              >
                {t('forgot-password')}
              </MuiLink>
            </div>

            {isNotConfirmed && (
              <Alert severity="error" className={loginNew.alert}>
                {t('confirm-first')}{' '}
                <Link
                  href={
                    watch('email')
                      ? `/confirm-email?email=${watch('email')}`
                      : '/confirm-email'
                  }
                  passHref
                >
                  <a>{t('here')}</a>
                </Link>
              </Alert>
            )}
            {isNeedToSetPassword && (
              <Alert severity="error" className={loginNew.alert}>
                {t('please-reset-password')}
                <MuiLink href={getResetPasswordUrl(watch('email'), true)}>
                  {t('here')}
                </MuiLink>
              </Alert>
            )}

            <button
              type="submit"
              className={clsx(
                loginNew.submit,
                isLoggingIn && loginNew.submitLoading
              )}
              disabled={alreadyLoggedIn || isLoggingIn}
              aria-busy={isLoggingIn}
            >
              {isLoggingIn ? (
                <>
                  <span className={loginNew.spinner} aria-hidden="true" />
                  {t('logging-in')}
                </>
              ) : (
                t('login')
              )}
            </button>
          </form>

          <p className={loginNew.footLinks}>
            {t('if-not-member')}{' '}
            <Link
              href={customerType === 'corporate' ? '/business' : withNew('/register')}
              passHref
            >
              <MuiLink>{t('signup')}</MuiLink>
            </Link>
            <br />
            <Link href={withNew('/privacy')} passHref>
              <MuiLink>{t('policy')}</MuiLink>
            </Link>
            <span className={loginNew.footSep}>•</span>
            <Link href={withNew('/terms')} passHref>
              <MuiLink>{t('terms-of-service')}</MuiLink>
            </Link>
          </p>
        </div>
      </div>
    </div>
  )

  // ?new: new-design chrome. NewShell already provides Layout + HeaderNew +
  // FooterNew, so it is NOT nested inside the old Layout.
  if (isNew) {
    return (
      <>
        <NewShell {...META}>{newContent}</NewShell>
        {renderSnackBar()}
      </>
    )
  }

  // Default (no ?new): original chrome, unchanged.
  return (
    <div className={classes.root}>
      <Layout noFooter noFloatingButtons {...META}>
        {content}
      </Layout>
      {renderSnackBar()}
    </div>
  )
}

export async function getServerSideProps(context) {
  return {
    props: {
      previousPath: context.req.headers.referer ?? '/',
      isNew: !('old' in context.query),
      ...(await serverSideTranslations(context.locale, [
        'common',
        'header',
        'footer',
        'pdpa',
        'login-signup',
      ])),
    },
  }
}

export default LoginPage
